import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import dayjs from "utils/dayjs";
import { Stack } from "components/layout/reusableLayouts";
import { Avatar } from "@outpave/ds";
import CustomSkeleton from "components/common/CustomSkeleton";
import { getDomain, logoLink } from "components/common/RenderMerchantDisplay";
import { formatCashFull } from "utils/formatCash";
import { ByocTransaction } from "hooks/useByocDashboard";

export interface RecentTransactionsListProps {
  transactions: ByocTransaction[];
  loading?: boolean;
  /** Horizontal inset, matched to whatever panel is hosting the list. */
  inset?: string;
}

/**
 * Transactions, with the row's transaction pre-opened in the review drawer.
 * That page already reads `id` off the query string via `useGetParamFromUrl`
 * and clears it on close, so this needs no new plumbing on either side.
 */
const expenseRoute = (transactionId: string) =>
  `/admin/transactions?id=${encodeURIComponent(transactionId)}`;

/** "Jordan Lee" from the transaction's user, or empty when unassigned. */
function cardholderName(transaction: ByocTransaction): string {
  const { firstName, lastName } = transaction.user ?? {};
  return [firstName, lastName].filter(Boolean).join(" ");
}

const TransactionRow: React.FC<{
  transaction: ByocTransaction;
  inset: string;
  onOpen: (transactionId: string) => void;
}> = ({ transaction, inset, onOpen }) => {
  const { t } = useTranslation();

  const merchantName =
    transaction.merchantData?.name || transaction.merchant || t("Unknown");

  // Only build the Brandfetch link when a URL exists: `getDomain("")`
  // constructs `new URL` on an empty value and throws. Without it the Avatar
  // falls back to initials.
  const merchantUrl = transaction.merchantData?.url;
  const logoSrc = merchantUrl
    ? logoLink(getDomain(merchantUrl), "icon", "lettermark", "light", 32)
    : undefined;

  // Card spend is stored negative. The row shows the magnitude with an explicit
  // sign, the way a statement reads.
  const isCredit = transaction.amount > 0;
  const formattedAmount = formatCashFull(
    Math.abs(transaction.amount),
    transaction.currency ?? "usd",
  );

  return (
    // A real <button> rather than a div with a click handler, so the row is
    // reachable by keyboard and announced as actionable without hand-rolling
    // role/tabIndex/keydown. The reset undoes the UA and Tailwind-preflight
    // button styles that would otherwise fight the row layout.
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(transaction.id)}
      aria-label={`${merchantName} ${formattedAmount}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-4)",
        width: "100%",
        padding: `var(--space-4) ${inset}`,
        font: "inherit",
        color: "inherit",
        textAlign: "left",
        background: "none",
        border: "none",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: "var(--color-bg-elevated)" },
        "&:focus-visible": {
          outline: "var(--border-width-2) solid var(--color-border-focus)",
          outlineOffset: "-2px",
        },
      }}
    >
      <Avatar size="sm" src={logoSrc} name={merchantName} alt={merchantName} />

      <Stack gap="var(--space-1)" sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h6" noWrap>
          {merchantName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)" }}
          noWrap
        >
          {cardholderName(transaction)}
        </Typography>
      </Stack>

      <Stack
        gap="var(--space-1)"
        sx={{ flexShrink: 0, alignItems: "flex-end" }}
      >
        <Typography
          variant="h6"
          sx={{
            whiteSpace: "nowrap",
            color: isCredit
              ? "var(--color-text-success)"
              : "var(--color-text-primary)",
          }}
        >
          {isCredit ? "+" : "-"}
          {formattedAmount}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-tertiary)", whiteSpace: "nowrap" }}
        >
          {transaction.transactionDate
            ? dayjs(transaction.transactionDate).format("MMM D")
            : ""}
        </Typography>
      </Stack>
    </Box>
  );
};

/**
 * Company-wide card activity, newest first, as a bare list.
 *
 * No card chrome of its own: it is rendered inside Connected accounts, under
 * that panel's own heading, so wrapping it again would nest two surfaces.
 */
const RecentTransactionsList: React.FC<RecentTransactionsListProps> = ({
  transactions,
  loading = false,
  inset = "var(--space-6)",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ padding: `var(--space-4) ${inset}` }}>
        <CustomSkeleton variant="receipt-transaction" rows={3} />
      </Box>
    );
  }

  if (transactions.length === 0) {
    return (
      <Typography
        variant="body1"
        sx={{
          color: "var(--color-text-tertiary)",
          textAlign: "center",
          padding: "var(--space-7) 0",
        }}
      >
        {t("No transactions yet")}
      </Typography>
    );
  }

  return (
    <Box>
      {transactions.map((transaction) => (
        <TransactionRow
          key={transaction.id}
          transaction={transaction}
          inset={inset}
          onOpen={(id) => navigate(expenseRoute(id))}
        />
      ))}
    </Box>
  );
};

export default RecentTransactionsList;
